import {useRef, forwardRef} from 'react'

import cn from '../className.js'
import {PRIVACY_MASK} from '../analytics/index.js'
import useAutoFocus from '../hooks/useAutoFocus.js'
import Icon from '../icons/Icon.js'

/**
 * A text input field
 *
 * @param {Object} props
 * @param {String|Boolean} [props.errorMessage]
 * @param {String} [props.id]
 * @param {String} [props.label]
 * @param {String} [props.labelClassName]
 * @param {String} props.value
 * @param {Function} props.onChange
 * @param {String} [props.type]
 * @param {String} [props.className]
 * @param {Boolean} [props.fsExclude]
 * @param {Boolean} [props.autoFocus]
 * @param {Boolean} [props.required]
 * @param {String|React.ReactNode} [props.prefix]
 * @param {String} [props.prefixClassName]
 * @param {String} [props.helpLink]
 * @param {String} [props.helpTitle]
 * @returns {React.ReactNode}
 */
const TextInput = forwardRef(
  (
    {
      errorMessage,
      id,
      label,
      labelClassName,
      onChange,
      type,
      fsExclude,
      className,
      autoFocus,
      required,
      prefix,
      prefixClassName,
      helpLink,
      helpTitle,
      ...props
    },
    inputRef,
  ) => {
    const backupInputRef = useRef()
    inputRef = inputRef || backupInputRef

    useAutoFocus(inputRef, autoFocus)

    const input = (
      <input
        ref={inputRef}
        className={cn`${className} ${fsExclude && PRIVACY_MASK} ${
          errorMessage && 'input--error'
        } ${prefix && 'input--w-prefix'}`}
        id={id}
        type={type || 'text'}
        onChange={(event) => onChange(event.target.value, event)}
        {...props}
      />
    )

    return (
      <>
        {label && id && (
          <label
            className={cn`${labelClassName} ${errorMessage && 'error'}`}
            htmlFor={id}
          >
            <span>{label}</span>
            {required ? <span className="required">*</span> : null}
            {helpLink ? (
              <a
                className="btn btn--link lh-sm no-underline light margin-left-3"
                href={helpLink}
                target="_blank"
                rel="noopener noreferrer"
                title={helpTitle}
              >
                <Icon name="question-circle" className="fs-01 lh-sm" />
              </a>
            ) : null}
          </label>
        )}
        {prefix ? (
          <div className="flex">
            <div
              className={cn`wrap--input-prefix flex--justify-col ${prefixClassName}`}
            >
              <strong className="fs-n1 text--md-grey">{prefix}</strong>
            </div>
            {input}
          </div>
        ) : (
          input
        )}
        {errorMessage && (
          <small className="error error-message">{errorMessage}</small>
        )}
      </>
    )
  },
)

TextInput.displayName = 'TextInput'

export default TextInput
